import React from 'react';
import { Box, Tooltip } from '@mui/material';

const RichTextRenderer = ({ 
  content = '', 
  maxWidth = '100%',
  maxHeight = '100px',
  imageMaxWidth = '100%',
  imageMaxHeight = '80px',
  videoMaxWidth = '100%',
  videoMaxHeight = '80px',
  showImages = true,
  showVideos = true,
  previewMode = false,
  previewLength = 150,
  tooltipMode = false,
  sx = {}
}) => {
  // Sanitize and process content
  const sanitizeContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Process images
    if (showImages) {
      const images = tempDiv.querySelectorAll('img');
      images.forEach(img => {
        img.style.maxWidth = imageMaxWidth;
        img.style.maxHeight = imageMaxHeight;
        img.style.width = 'auto';
        img.style.height = 'auto';
        img.style.borderRadius = '4px';
        img.style.margin = '4px 0';
        img.style.display = 'block';
        img.style.objectFit = 'contain';
      });
    } else {
      // Remove images if not showing
      const images = tempDiv.querySelectorAll('img');
      images.forEach(img => img.remove());
    }
    
    // Process videos
    if (showVideos) {
      const videos = tempDiv.querySelectorAll('video');
      videos.forEach(video => {
        video.style.maxWidth = videoMaxWidth;
        video.style.maxHeight = videoMaxHeight;
        video.style.width = 'auto';
        video.style.height = 'auto';
        video.style.borderRadius = '4px';
        video.style.margin = '4px 0';
        video.style.display = 'block';
        video.style.objectFit = 'contain';
      });
    } else {
      // Remove videos if not showing
      const videos = tempDiv.querySelectorAll('video');
      videos.forEach(video => video.remove());
    }
    
    // Process iframes (embeds)
    const iframes = tempDiv.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      iframe.style.maxWidth = imageMaxWidth;
      iframe.style.maxHeight = imageMaxHeight;
      iframe.style.width = '100%';
      iframe.style.height = 'auto';
      iframe.style.aspectRatio = '16/9';
      iframe.style.borderRadius = '4px';
      iframe.style.border = 'none';
      iframe.style.margin = '4px 0';
      iframe.style.display = 'block';
    });
    
    // Process links
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      if (tooltipMode) {
        link.style.color = '#90caf9';
        link.style.textDecoration = 'none';
      }
    });
    
    // Process paragraphs
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      p.style.margin = '4px 0';
      p.style.wordBreak = 'break-word';
    });
    
    // Process tables
    const tables = tempDiv.querySelectorAll('table');
    tables.forEach(table => {
      table.style.borderCollapse = 'collapse';
      table.style.margin = '8px 0';
      
      const cells = table.querySelectorAll('th, td');
      cells.forEach(cell => {
        cell.style.border = tooltipMode ? '1px solid #555' : '1px solid #ddd';
        cell.style.padding = '4px 8px';
        cell.style.fontSize = tooltipMode ? '12px' : '14px';
        if (tooltipMode) {
          cell.style.color = 'white';
        }
      });
    });
    
    return tempDiv.innerHTML;
  };

  // Get preview text if in preview mode
  const getPreviewText = (htmlContent) => {
    if (!previewMode || !htmlContent) return htmlContent;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent?.length <= previewLength) return htmlContent;
    
    return textContent.substring(0, previewLength) + '...';
  };

  const processedContent = sanitizeContent(content);
  const displayContent = getPreviewText(processedContent);

  // Tooltip styles
  const tooltipStyles = {
    tooltipContent: {
      maxWidth: '350px',
      maxHeight: '400px',
      overflow: 'auto',
      color: 'white',
      fontSize: '12px',
      lineHeight: '1.4',
      padding: '8px',
    },
    tooltipContainer: {
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      color: 'white',
      maxWidth: '350px',
      maxHeight: '400px',
      overflow: 'auto',
      padding: '12px',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      fontSize: '12px',
      '& img': {
        maxWidth: '250px',
        maxHeight: '150px',
        width: 'auto',
        height: 'auto',
        borderRadius: '4px',
        margin: '4px 0',
        display: 'block',
      },
      '& video': {
        maxWidth: '250px',
        maxHeight: '150px',
        width: 'auto',
        height: 'auto',
        borderRadius: '4px',
        margin: '4px 0',
        display: 'block',
      },
      '& iframe': {
        maxWidth: '250px',
        maxHeight: '150px',
        width: '100%',
        height: 'auto',
        aspectRatio: '16/9',
        borderRadius: '4px',
        border: 'none',
        margin: '4px 0',
        display: 'block',
      },
      '& a': {
        color: '#90caf9',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      '& p': {
        margin: '4px 0',
        wordBreak: 'break-word',
      },
      '& table': {
        borderCollapse: 'collapse',
        margin: '8px 0',
        '& th, & td': {
          border: '1px solid #555',
          padding: '4px 8px',
          fontSize: '12px',
          color: 'white',
        },
      },
    },
    tooltipWrapper: {
      cursor: 'pointer',
    },
  };

  if (tooltipMode) {
    return (
      <Tooltip 
        title={
          <div style={tooltipStyles.tooltipContent}>
            <div dangerouslySetInnerHTML={{ __html: processedContent }} />
          </div>
        }
        placement="bottom"
        arrow
        enterDelay={300}
        leaveDelay={0}
        PopperProps={{
          sx: {
            '& .MuiTooltip-tooltip': tooltipStyles.tooltipContainer
          }
        }}
      >
        <Box sx={tooltipStyles.tooltipWrapper}>
          <div 
            dangerouslySetInnerHTML={{ __html: displayContent }}
            style={{
              maxWidth,
              maxHeight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.2',
              fontFamily: 'Lexend',
              fontSize: '14px',
              color: '#0F0F0F',
              whiteSpace: 'normal',
              ...sx
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <div 
      dangerouslySetInnerHTML={{ __html: displayContent }}
      style={{
        maxWidth,
        maxHeight,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 5,
        WebkitBoxOrient: 'vertical',
        lineHeight: '1.2',
        fontFamily: 'Lexend',
        fontSize: '14px',
        color: '#0F0F0F',
        whiteSpace: 'normal',
        ...sx
      }}
    />
  );
};

export default RichTextRenderer; 